import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import {
  Search, Loader2, Bug, User, Calendar, Ticket, Image, Hash,
  Globe, ShoppingBag, Mail, Shield, MoreHorizontal, AlertTriangle,
  ExternalLink, LayoutDashboard, Clock, XCircle, AlertCircle, CheckCircle2, Info,
  Eye,
} from 'lucide-react';

import { AdminLayout } from '@/components/admin/AdminLayout';
import { PageHeader } from '@/components/admin/PageHeader';
import { StudioSurface } from '@/components/admin/StudioSurface';
import { EmptyBlock } from '@/components/admin/EmptyBlock';
import { SystemEventLevelBadge } from '@/components/admin/SystemEventLevelBadge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

import {
  useHostDebugSearch, useHostSystemEvents, useHostActivities,
  useHostBookings, useHostMediaErrors, useRequestIdLookup,
  useHostErrorSummary, useHostTimeline,
  type HostDebugProducer, type TimelineItem,
} from '@/hooks/useHostDebug';
import type { SystemEvent } from '@/hooks/useSystemEvents';
import {
  classifyEventKey, groupByDomain, DOMAIN_META,
  type EventDomain,
} from '@/lib/observability/eventKeyDomains';

const DOMAIN_ICONS: Record<EventDomain, React.ReactNode> = {
  activities: <Calendar className="h-4 w-4" />,
  publishing: <Globe className="h-4 w-4" />,
  bookings: <Ticket className="h-4 w-4" />,
  media: <Image className="h-4 w-4" />,
  orders: <ShoppingBag className="h-4 w-4" />,
  contact: <Mail className="h-4 w-4" />,
  admin: <Shield className="h-4 w-4" />,
  other: <MoreHorizontal className="h-4 w-4" />,
};

type LevelFilter = 'all' | 'error' | 'warn' | 'info';

/** All 5 key domains, always shown (even if 0) */
const SUMMARY_DOMAINS: EventDomain[] = ['activities', 'publishing', 'bookings', 'media', 'orders'];

export default function AdminHostDebugPage() {
  const [searchParams] = useSearchParams();
  const urlQ = searchParams.get('q') ?? '';

  const [searchInput, setSearchInput] = useState(urlQ);
  const [searchQuery, setSearchQuery] = useState(urlQ);
  const [selectedProducer, setSelectedProducer] = useState<HostDebugProducer | null>(null);
  const [requestIdInput, setRequestIdInput] = useState('');
  const [requestIdQuery, setRequestIdQuery] = useState<string | null>(null);
  const [domainFilter, setDomainFilter] = useState<EventDomain | 'all'>('all');
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('all');

  // Track last auto-selected q to avoid loops
  const autoSelectedRef = useRef<string | null>(null);

  const { data: searchResults, isLoading: searching } = useHostDebugSearch(searchQuery);
  const producerId = selectedProducer?.id ?? null;
  const { data: systemEvents, isLoading: loadingEvents } = useHostSystemEvents(producerId);
  const { data: activities, isLoading: loadingActivities } = useHostActivities(producerId);
  const { data: bookings, isLoading: loadingBookings } = useHostBookings(producerId);
  const { data: mediaErrors } = useHostMediaErrors(producerId);
  const { data: errorSummaryEvents } = useHostErrorSummary(producerId);
  const { data: requestIdResults, isLoading: loadingRequestId } = useRequestIdLookup(requestIdQuery);
  const { data: timeline, isLoading: loadingTimeline } = useHostTimeline(producerId);

  // Auto-select producer when search results come back from querystring-initiated search
  useEffect(() => {
    if (
      urlQ &&
      searchResults &&
      searchResults.length === 1 &&
      !selectedProducer &&
      autoSelectedRef.current !== urlQ
    ) {
      autoSelectedRef.current = urlQ;
      setSelectedProducer(searchResults[0]);
      setSearchQuery(''); // Clear to hide result list
    }
  }, [urlQ, searchResults, selectedProducer]);

  // Sync input if URL changes while on page
  useEffect(() => {
    if (urlQ && urlQ !== searchInput && !selectedProducer) {
      setSearchInput(urlQ);
      setSearchQuery(urlQ);
      autoSelectedRef.current = null; // Allow re-auto-select
    }
  }, [urlQ]);

  // Domain-grouped error summary (last 7 days) — always show all 5 key domains
  const domainSummary = useMemo(() => {
    const counts: Record<EventDomain, number> = {
      activities: 0, publishing: 0, bookings: 0, media: 0, orders: 0,
      contact: 0, admin: 0, other: 0,
    };
    if (!errorSummaryEvents) return counts;
    for (const ev of errorSummaryEvents) {
      counts[classifyEventKey(ev.event_key)]++;
    }
    return counts;
  }, [errorSummaryEvents]);

  // Filtered + sorted system events (errors first, then level filter, then domain)
  const filteredEvents = useMemo(() => {
    if (!systemEvents) return [];
    let events = [...systemEvents];

    // Domain filter
    if (domainFilter !== 'all') {
      events = events.filter((ev) => classifyEventKey(ev.event_key) === domainFilter);
    }

    // Level filter
    if (levelFilter !== 'all') {
      events = events.filter((ev) => ev.level === levelFilter);
    }

    // Sort: errors first, then warnings, then info
    const levelOrder = { error: 0, warn: 1, info: 2 };
    events.sort((a, b) => {
      const la = levelOrder[a.level as keyof typeof levelOrder] ?? 3;
      const lb = levelOrder[b.level as keyof typeof levelOrder] ?? 3;
      if (la !== lb) return la - lb;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return events;
  }, [systemEvents, domainFilter, levelFilter]);

  // Level counts for tabs
  const levelCounts = useMemo(() => {
    if (!systemEvents) return { error: 0, warn: 0, info: 0 };
    const c = { error: 0, warn: 0, info: 0 };
    for (const ev of systemEvents) {
      if (ev.level in c) c[ev.level as keyof typeof c]++;
    }
    return c;
  }, [systemEvents]);

  const handleSearch = () => {
    setSearchQuery(searchInput.trim());
    setSelectedProducer(null);
  };

  const handleSelectProducer = (p: HostDebugProducer) => {
    setSelectedProducer(p);
    setSearchQuery('');
    setDomainFilter('all');
    setLevelFilter('all');
  };

  const handleRequestIdSearch = () => {
    setRequestIdQuery(requestIdInput.trim() || null);
  };

  const fmtDate = (d: string) => {
    try {
      return format(new Date(d), 'd MMM yyyy HH:mm', { locale: sv });
    } catch {
      return d;
    }
  };

  return (
    <AdminLayout title="Host Debug">
      <PageHeader
        title="Host Debug"
        description="Felsök värdproblem snabbt. Sök på producer_id, slug eller e-post."
      />

      <div className="px-4 lg:px-8 pb-8 space-y-6">
        {/* ─── Search ─── */}
        <StudioSurface>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Sök producer_id, slug eller e-post…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9"
              />
            </div>
            <Button onClick={handleSearch} disabled={searchInput.trim().length < 2}>
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sök'}
            </Button>
          </div>

          {searchResults && searchResults.length > 0 && !selectedProducer && (
            <div className="mt-3 border rounded-lg divide-y">
              {searchResults.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handleSelectProducer(p)}
                  className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors flex items-center justify-between gap-2"
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground font-mono truncate">{p.id}</p>
                  </div>
                  <Badge variant={p.status === 'public' ? 'default' : 'secondary'}>
                    {p.status}
                  </Badge>
                </button>
              ))}
            </div>
          )}
          {searchResults && searchResults.length === 0 && searchQuery && (
            <p className="mt-3 text-sm text-muted-foreground">Ingen värd hittades för &quot;{searchQuery}&quot;</p>
          )}
        </StudioSurface>

        {/* ─── Selected Producer ─── */}
        {selectedProducer && (
          <>
            {/* Summary + quick links */}
            <StudioSurface>
              <div className="flex items-start gap-3 mb-4">
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-semibold">{selectedProducer.name}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-sm">
                    <InfoRow label="ID" value={selectedProducer.id} mono />
                    <InfoRow label="Status" value={selectedProducer.status} />
                    <InfoRow label="Slug" value={selectedProducer.slug ?? '–'} />
                    <InfoRow label="Place ID" value={selectedProducer.place_id ?? '–'} mono />
                    <InfoRow label="E-post" value={selectedProducer.email ?? '–'} />
                    <InfoRow label="Skapad" value={fmtDate(selectedProducer.created_at)} />
                  </div>

                  {/* Quick navigation */}
                  <div className="flex flex-wrap gap-2 mt-3">
                     <Button variant="outline" size="sm" asChild>
                       <Link to={`/app/admin/producers/${selectedProducer.id}`}>
                         <LayoutDashboard className="h-3.5 w-3.5 mr-1.5" />
                         Öppna värdportal
                       </Link>
                     </Button>
                     <Button variant="outline" size="sm" asChild>
                       <a
                         href={`/app/producer/farm/${selectedProducer.id}?support_preview=1`}
                         target="_blank"
                         rel="noopener noreferrer"
                       >
                         <Eye className="h-3.5 w-3.5 mr-1.5" />
                         Visa som värd
                       </a>
                     </Button>
                     {selectedProducer.status === 'public' && (
                       <Button variant="outline" size="sm" asChild>
                         <Link to={`/producer/${selectedProducer.id}`} target="_blank">
                           <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                           Öppna publika sidan
                         </Link>
                       </Button>
                     )}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedProducer(null)}>
                  Rensa
                </Button>
              </div>
            </StudioSurface>

            {/* ─── Activity Timeline ─── */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Clock className="h-4 w-4" />
                  Senaste aktivitet
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingTimeline ? (
                  <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
                ) : timeline && timeline.length > 0 ? (
                  <div className="relative space-y-0">
                    {/* vertical line */}
                    <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border" />
                    {timeline.map((item) => (
                      <TimelineRow key={item.id} item={item} fmtDate={fmtDate} />
                    ))}
                  </div>
                ) : (
                  <EmptyBlock title="Ingen aktivitet" description="Ingen registrerad aktivitet för denna värd." />
                )}
              </CardContent>
            </Card>

            {/* ─── Problem Summary (7 days) — always visible ─── */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4" />
                Problem senaste 7 dagarna
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {SUMMARY_DOMAINS.map((domain) => {
                  const count = domainSummary[domain];
                  return (
                    <button
                      key={domain}
                      onClick={() => setDomainFilter(domainFilter === domain ? 'all' : domain)}
                      className={`rounded-lg border p-3 text-left transition-colors hover:bg-muted/50 ${
                        domainFilter === domain ? 'border-primary bg-primary/5' : ''
                      } ${count > 0 ? '' : 'opacity-60'}`}
                    >
                      <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                        {DOMAIN_ICONS[domain]}
                        <span className="text-xs">{DOMAIN_META[domain].label}</span>
                      </div>
                      <p className={`text-2xl font-bold tabular-nums ${count > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                        {count}
                      </p>
                    </button>
                  );
                })}
              </div>
              {domainFilter !== 'all' && (
                <Button variant="ghost" size="sm" className="mt-2" onClick={() => setDomainFilter('all')}>
                  Visa alla domäner
                </Button>
              )}
            </div>

            {/* ─── System Events (with level + domain filter) ─── */}
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Bug className="h-4 w-4" />
                    System Events ({filteredEvents.length})
                    {domainFilter !== 'all' && (
                      <Badge variant="outline" className="ml-1 text-xs">
                        {DOMAIN_META[domainFilter].label}
                      </Badge>
                    )}
                  </CardTitle>
                  <Tabs value={levelFilter} onValueChange={(v) => setLevelFilter(v as LevelFilter)}>
                    <TabsList className="h-8">
                      <TabsTrigger value="all" className="text-xs px-2 h-7">Alla</TabsTrigger>
                      <TabsTrigger value="error" className="text-xs px-2 h-7">
                        Fel {levelCounts.error > 0 && <span className="ml-1 tabular-nums">({levelCounts.error})</span>}
                      </TabsTrigger>
                      <TabsTrigger value="warn" className="text-xs px-2 h-7">
                        Varning {levelCounts.warn > 0 && <span className="ml-1 tabular-nums">({levelCounts.warn})</span>}
                      </TabsTrigger>
                      <TabsTrigger value="info" className="text-xs px-2 h-7">Info</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </CardHeader>
              <CardContent>
                {loadingEvents ? (
                  <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
                ) : filteredEvents.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tid</TableHead>
                          <TableHead>Nivå</TableHead>
                          <TableHead>Domän</TableHead>
                          <TableHead>event_key</TableHead>
                          <TableHead className="hidden md:table-cell">entity_id</TableHead>
                          <TableHead className="hidden lg:table-cell">message</TableHead>
                          <TableHead className="hidden md:table-cell">request_id</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredEvents.map((ev) => (
                          <SystemEventRow key={ev.id} event={ev} fmtDate={fmtDate} />
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <EmptyBlock
                    title="Inga fel registrerade"
                    description={
                      levelFilter !== 'all' || domainFilter !== 'all'
                        ? 'Inga händelser matchar ditt filter.'
                        : 'Inga fel registrerade för denna värd.'
                    }
                  />
                )}
              </CardContent>
            </Card>

            {/* ─── Activities ─── */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calendar className="h-4 w-4" />
                  Aktiviteter ({activities?.length ?? 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingActivities ? (
                  <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
                ) : activities && activities.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Titel</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Startdatum</TableHead>
                          <TableHead className="text-center">Platser</TableHead>
                          <TableHead className="text-center">Bokningar</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activities.map((a) => (
                          <TableRow key={a.id}>
                            <TableCell className="font-medium max-w-[200px] truncate">{a.title}</TableCell>
                            <TableCell>
                              <Badge variant={a.status === 'public' ? 'default' : 'secondary'}>{a.status}</Badge>
                            </TableCell>
                            <TableCell>{fmtDate(a.start_date)}</TableCell>
                            <TableCell className="text-center">{a.max_spots}</TableCell>
                            <TableCell className="text-center">{a.booking_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <EmptyBlock title="Inga aktiviteter" description="Denna värd har inte skapat några aktiviteter ännu." />
                )}
              </CardContent>
            </Card>

            {/* ─── Bookings ─── */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Ticket className="h-4 w-4" />
                  Senaste bokningar ({bookings?.length ?? 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingBookings ? (
                  <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
                ) : bookings && bookings.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>E-post</TableHead>
                          <TableHead>Event ID</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-center">Platser</TableHead>
                          <TableHead>Bokad</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bookings.map((b) => (
                          <TableRow key={b.id}>
                            <TableCell className="max-w-[180px] truncate">{b.guest_email}</TableCell>
                            <TableCell className="font-mono text-xs max-w-[100px] truncate">{b.event_id}</TableCell>
                            <TableCell>
                              <Badge variant={b.status === 'confirmed' ? 'default' : 'secondary'}>{b.status}</Badge>
                            </TableCell>
                            <TableCell className="text-center">{b.spots}</TableCell>
                            <TableCell>{fmtDate(b.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <EmptyBlock title="Inga bokningar" description="Denna värd har inga bokningar ännu." />
                )}
              </CardContent>
            </Card>

            {/* ─── Media Errors ─── */}
            {mediaErrors && mediaErrors.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Image className="h-4 w-4" />
                    Media-fel ({mediaErrors.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tid</TableHead>
                          <TableHead>Nivå</TableHead>
                          <TableHead>event_key</TableHead>
                          <TableHead className="hidden md:table-cell">message</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {mediaErrors.map((ev) => (
                          <TableRow key={ev.id}>
                            <TableCell className="text-xs whitespace-nowrap">{fmtDate(ev.created_at)}</TableCell>
                            <TableCell><SystemEventLevelBadge level={ev.level} /></TableCell>
                            <TableCell className="font-mono text-xs">{ev.event_key}</TableCell>
                            <TableCell className="hidden md:table-cell text-xs max-w-[300px] truncate">{ev.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* ─── Request ID Lookup ─── */}
        <StudioSurface>
          <h3 className="text-base font-semibold flex items-center gap-2 mb-3">
            <Hash className="h-4 w-4" />
            Request ID Lookup
          </h3>
          <div className="flex gap-2">
            <Input
              placeholder="Klistra in request_id (UUID)…"
              value={requestIdInput}
              onChange={(e) => setRequestIdInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRequestIdSearch()}
              className="font-mono text-sm"
            />
            <Button onClick={handleRequestIdSearch} disabled={requestIdInput.trim().length < 8}>
              {loadingRequestId ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sök'}
            </Button>
          </div>

          {requestIdResults && requestIdResults.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tid</TableHead>
                    <TableHead>Nivå</TableHead>
                    <TableHead>Domän</TableHead>
                    <TableHead>event_key</TableHead>
                    <TableHead className="hidden md:table-cell">producer_id</TableHead>
                    <TableHead className="hidden lg:table-cell">message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requestIdResults.map((ev) => (
                    <TableRow key={ev.id}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(ev.created_at)}</TableCell>
                      <TableCell><SystemEventLevelBadge level={ev.level} /></TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          {DOMAIN_ICONS[classifyEventKey(ev.event_key)]}
                          {DOMAIN_META[classifyEventKey(ev.event_key)].label}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{ev.event_key}</TableCell>
                      <TableCell className="hidden md:table-cell font-mono text-xs max-w-[100px] truncate">{ev.producer_id ?? '–'}</TableCell>
                      <TableCell className="hidden lg:table-cell text-xs max-w-[300px] truncate">{ev.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {requestIdResults && requestIdResults.length === 0 && requestIdQuery && (
            <p className="mt-3 text-sm text-muted-foreground">Inga händelser för request_id &quot;{requestIdQuery}&quot;</p>
          )}
        </StudioSurface>
      </div>
    </AdminLayout>
  );
}

/* ── Helper components ── */

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground">{label}:</span>
      <span className={mono ? 'font-mono text-xs break-all' : ''}>{value}</span>
    </div>
  );
}

function SystemEventRow({ event, fmtDate }: { event: SystemEvent; fmtDate: (d: string) => string }) {
  const domain = classifyEventKey(event.event_key);
  return (
    <TableRow>
      <TableCell className="text-xs whitespace-nowrap">{fmtDate(event.created_at)}</TableCell>
      <TableCell><SystemEventLevelBadge level={event.level} /></TableCell>
      <TableCell>
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          {DOMAIN_ICONS[domain]}
          {DOMAIN_META[domain].label}
        </span>
      </TableCell>
      <TableCell className="font-mono text-xs">{event.event_key}</TableCell>
      <TableCell className="hidden md:table-cell font-mono text-xs max-w-[100px] truncate">{event.entity_id ?? '–'}</TableCell>
      <TableCell className="hidden lg:table-cell text-xs max-w-[300px] truncate">{event.message}</TableCell>
      <TableCell className="hidden md:table-cell font-mono text-xs max-w-[80px] truncate">
        {event.request_id ? event.request_id.slice(0, 8) + '…' : '–'}
      </TableCell>
    </TableRow>
  );
}

const TIMELINE_LEVEL_ICON: Record<TimelineItem['level'], React.ReactNode> = {
  error: <XCircle className="h-4 w-4 text-destructive" />,
  warn: <AlertCircle className="h-4 w-4 text-amber-500" />,
  success: <CheckCircle2 className="h-4 w-4 text-primary" />,
  info: <Info className="h-4 w-4 text-muted-foreground" />,
};

const SOURCE_LABEL: Record<TimelineItem['source'], string> = {
  system_event: 'System',
  activity: 'Aktivitet',
  booking: 'Bokning',
  media: 'Media',
};

function TimelineRow({ item, fmtDate }: { item: TimelineItem; fmtDate: (d: string) => string }) {
  return (
    <div className="relative flex gap-3 pl-0 py-2 group">
      {/* Icon dot */}
      <div className="relative z-10 flex-shrink-0 w-[22px] flex justify-center">
        {TIMELINE_LEVEL_ICON[item.level]}
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0 pb-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium truncate">{item.title}</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
            {SOURCE_LABEL[item.source]}
          </Badge>
        </div>
        {item.detail && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[400px]">{item.detail}</p>
        )}
        <p className="text-[10px] text-muted-foreground mt-0.5">{fmtDate(item.timestamp)}</p>
      </div>
    </div>
  );
}
